import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EntityCard } from '@/components/EntityCard';
import { styleTemplatesPageMessages } from './styleTemplatesPage.messages';
import { fetchStyleTemplates } from '@/api/services/style-templates/fetchStyleTemplates';
import type { StyleTemplateWithUrls } from 'schemas';
import { ApiError } from '@/api/ApiError';
import { UNKNOWN_ERROR_API_ERROR } from '@/api/helpers/unknownError';
import { Button } from '@/components/ui/button';
import { Heading3, Muted } from '@/components/Typography';
import { ROUTES } from '@/routes';
import { DashboardPageContainer } from '@/components/layout/DashboardPageContainer';
import { CardsGridContainer } from '@/components/layout/CardsGridContainer';
import { transformStyleTemplateToEntityCard } from '@/pages/StyleTemplatesPage/helpers/transformStyleTemplateToEntityCard';
import { useLoadOnce } from '@/hooks/useLoadOnce';
import { toast } from 'sonner';

export function StyleTemplatesPage() {
  const navigate = useNavigate();
  const [styleTemplates, setStyleTemplates] = useState<StyleTemplateWithUrls[]>(
    []
  );
  const [loading, setLoading] = useState(true);

  const goToCreateTemplate = () => {
    navigate(ROUTES.DASHBOARD_STYLE_TEMPLATES_CREATE);
  };

  useLoadOnce(() => {
    const loadStyleTemplates = async () => {
      try {
        setLoading(true);
        const fetchedTemplates = await fetchStyleTemplates();
        setStyleTemplates(fetchedTemplates);
      } catch (err) {
        const apiError =
          err instanceof ApiError ? err : UNKNOWN_ERROR_API_ERROR;
        toast.error(apiError.message);
      } finally {
        setLoading(false);
      }
    };

    loadStyleTemplates();
  });

  return (
    <DashboardPageContainer
      headline={styleTemplatesPageMessages.title}
      subheading={styleTemplatesPageMessages.subtitle}
      cta={
        <Button onClick={goToCreateTemplate}>
          {styleTemplatesPageMessages.createButton}
        </Button>
      }
    >
      {styleTemplates.length === 0 && !loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Heading3 className="mb-2">
              {styleTemplatesPageMessages.noTemplatesFound}
            </Heading3>
            <Muted>{styleTemplatesPageMessages.noTemplatesDescription}</Muted>
          </div>
        </div>
      ) : (
        <CardsGridContainer loading={loading}>
          {styleTemplates.map((template) => {
            const entityCardProps =
              transformStyleTemplateToEntityCard(template);
            return <EntityCard key={template.id} {...entityCardProps} />;
          })}
        </CardsGridContainer>
      )}
    </DashboardPageContainer>
  );
}
